import { useState } from 'react';
import { toast } from 'react-toastify';
import { useBlockEditFormStore } from '@ContractBuilder/modules/block-edit';
import { useEntityStore } from '@ContractBuilder/store';
import type { LibraryBlockTypeForListPage } from '@ContractBuilder/types';
import { isTemplatePath } from '@utils/app-paths';

import { useBlockCreator } from '../context/context';

export const useLibraryInsert = () => {
  const { handleCancel } = useBlockCreator();
  const { formValues } = useBlockEditFormStore(({ formValues }) => ({ formValues }));
  const { addBlockFromLibraryToSubmission, addBlockFromLibraryToTemplate, addBlocksFromLibrary } = useEntityStore(
    ({ addBlockFromLibraryToSubmission, addBlockFromLibraryToTemplate, addBlocksFromLibrary }) => ({
      addBlockFromLibraryToSubmission,
      addBlockFromLibraryToTemplate,
      addBlocksFromLibrary,
    }),
  );
  const [isInserting, setIsInserting] = useState(false);

  const onInsertBlock = async (blockLibraryId: string, block: LibraryBlockTypeForListPage | null) => {
    const startingSectionId = formValues?.section_id;
    const startingOrder = formValues?.order;

    setIsInserting(true);
    const position = { blockLibraryId, sectionId: formValues?.section_id ?? '', order: (formValues?.order ?? 0) + 1 };
    const add = isTemplatePath()
      ? addBlockFromLibraryToTemplate(position)
      : addBlockFromLibraryToSubmission({ ...position, canDelete: block?.canDelete, canEdit: block?.canEdit });
    const inserted = await add.catch(() => false);
    setIsInserting(false);

    if (!inserted) {
      toast.error('Failed to add the block from the library. Please try again.');
      return;
    }

    const currentFormValues = useBlockEditFormStore.getState().formValues;
    if (currentFormValues?.section_id === startingSectionId && currentFormValues?.order === startingOrder) {
      handleCancel();
    }
  };

  const onInsertSelectedBlocks = async (blockLibraryIds: string[]) => {
    if (blockLibraryIds.length === 0) {
      return false;
    }

    const startingSectionId = formValues?.section_id;
    const startingOrder = formValues?.order;

    setIsInserting(true);
    const inserted = await addBlocksFromLibrary({
      blockLibraryIds,
      sectionId: startingSectionId ?? '',
      order: (startingOrder ?? 0) + 1,
    }).catch(() => false);
    setIsInserting(false);

    if (!inserted) {
      toast.error('Failed to add the selected blocks from the library. Please try again.');
      return false;
    }

    toast.success(
      `Added ${blockLibraryIds.length} ${blockLibraryIds.length === 1 ? 'block' : 'blocks'} from the library`,
    );

    const currentFormValues = useBlockEditFormStore.getState().formValues;
    if (currentFormValues?.section_id === startingSectionId && currentFormValues?.order === startingOrder) {
      handleCancel();
    }

    return true;
  };

  return { isInserting, onInsertBlock, onInsertSelectedBlocks };
};
